import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { useAuthContext } from "../../../app/providers/contextIndex";
import { useNavigate } from "react-router-dom";
import { getInfoCliente } from "../../../services/backend/clienteService";
import { getAffariClienteCodigo, getAffariCanal, } from "../../../services/backend/authService";
import { getEditarUsuario, grabarUsuario, } from "../../../services/backend/adminService";
import { actualizarMarketing, getAceptaMarketing, } from "../../../services/backend/usuarioService";
import { getReporteCotizacion } from "../../../services/backend/reportService";
import useMobile from "../../../utils/useMobile";
import { StorageKeys, session } from "../../../utils/storage";
import { sanitizePhoneNumber, isValidEmail } from "../../../utils/validation";
import { validatePasswordWithFeedback } from "../../auth/utils/loginValidation";
import AppToast from "../../../shared/components/AppToast";
import PersonalInfoForm from "../components/PersonalInfoForm";
import SecurityForm from "../components/SecurityForm";
import PurchaseHistory from "../components/PurchaseHistory";
import AccountBalance from "../components/AccountBalance";
import HelpSection from "../components/HelpSection";
import "../../content/styles/admin-paginas.css";

const CLIENT_SECTIONS = [
  { id: "personal", label: "Mi perfil", icon: "pi pi-user" },
  { id: "purchases", label: "Mis compras", icon: "pi pi-shopping-cart" },
  { id: "balance", label: "Estado de cuenta", icon: "pi pi-wallet" },
  { id: "help", label: "¿Necesitas ayuda?", icon: "pi pi-question-circle" },
];

const ADMIN_SECTIONS = [
  { id: "personal", label: "Mi perfil", icon: "pi pi-user" },
];

const getDefaultPurchasesRange = () => {
  const hasta = new Date();
  hasta.setHours(0, 0, 0, 0);
  const desde = new Date(hasta);
  desde.setDate(desde.getDate() - 30);
  return { desde, hasta };
};

const formatHistoryDate = (value) => {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return "";
  const day = String(value.getDate()).padStart(2, "0");
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const year = value.getFullYear();
  return `${year}-${month}-${day}`;
};

const MyAccount = () => {
  const navigate = useNavigate();
  const { appPerfil, appUser, appRolCredito, startSession } = useAuthContext();
  const isMobile = useMobile();

  const accountSections = appPerfil === "Administrador" ? ADMIN_SECTIONS : CLIENT_SECTIONS;
  const [activeSection, setActiveSection] = useState("personal");
  const [personalForm, setPersonalForm] = useState({ nombre: "", telefono: "", email: "", });
  const [perfilForm, setPerfilForm] = useState({ descripcion: "" });
  const [accessForm, setAccessForm] = useState(() => ({
    usuario: session.get(StorageKeys.AUTH_USERNAME) || (appUser && !appUser.includes("@") ? appUser : ""),
    contrasenaActual: "",
    contrasena: "",
    confirmarContrasena: "",
  }));
  const [isSavingPersonal, setIsSavingPersonal] = useState(false);
  const [isSavingAccess, setIsSavingAccess] = useState(false);
  const [purchasesFilters, setPurchasesFilters] = useState(getDefaultPurchasesRange);
  const [purchaseHistory, setPurchaseHistory] = useState([]);
  const [historyNotice, setHistoryNotice] = useState("");
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [clientInfo, setClientInfo] = useState(null);
  const [clientData, setClientData] = useState(null);
  const [deudaCliente, setDeudaCliente] = useState([]);
  const [isLoadingClientInfo, setIsLoadingClientInfo] = useState(false);
  const [sidebarSearchTerm, setSidebarSearchTerm] = useState("");
  const [perfilId, setPerfilId] = useState(0);
  const [marketingAccepted, setMarketingAccepted] = useState(false);

  const userId = Number(session.get(StorageKeys.AUTH_ID));
  const userCodigo = getAffariClienteCodigo();
  const toastRef = useRef(null);

  const updatePersonalForm = (field, value) =>
    setPersonalForm((prev) => ({ ...prev, [field]: value }));
  const updateAccessForm = (field, value) =>
    setAccessForm((prev) => ({ ...prev, [field]: value }));
  const updatePurchasesFilter = (field, value) =>
    setPurchasesFilters((prev) => ({ ...prev, [field]: value }));

  const filteredAccountSections = useMemo(() => {
    if (!sidebarSearchTerm.trim()) return accountSections;
    const term = sidebarSearchTerm.toLowerCase();
    return accountSections.filter((section) =>
      section.label.toLowerCase().includes(term)
    );
  }, [sidebarSearchTerm, accountSections]);

  const buildUserPayload = (alter = false) => ({
    id: userId,
    nombre: personalForm.nombre.trim(),
    usuario: accessForm.usuario.trim(),
    contrasena: accessForm.contrasena.trim(),
    email: personalForm.email.trim(),
    telefono: sanitizePhoneNumber(personalForm.telefono),
    perfil: perfilId,
    activo: 1,
    alter,
  });

  useEffect(() => {
    if (!userId) return;
    const loadUser = async () => {
      try {
        const response = await getEditarUsuario(userId);
        const usuario = response?.usuario;
        if (!usuario) return;
        setPersonalForm((prev) => ({
          ...prev,
          nombre: usuario.nombre || prev.nombre,
          telefono: usuario.telefono
            ? sanitizePhoneNumber(usuario.telefono)
            : prev.telefono,
          email: usuario.email || prev.email,
        }));
        setAccessForm((prev) => ({
          ...prev,
          usuario: usuario.usuario || prev.usuario,
        }));
        setPerfilId(Number(usuario?.perfil?.id) || 0);
        const perfil = usuario.perfil;
        if (!perfil) return;
        setPerfilForm((prev) => ({
          ...prev,
          descripcion: perfil.descripcion || prev.descripcion,
        }));
      } catch {
        /* silent */
      }
    };
    loadUser();
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    getAceptaMarketing(userId)
      .then((response) => {
        if (cancelled) return;
        setMarketingAccepted(String(response).trim().toLowerCase() === "true");
      })
      .catch(() => {
        if (cancelled) return;
        setMarketingAccepted(false);
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const handleMarketingChange = async (checked) => {
    if (!userId) {
      toastRef.current?.show({
        severity: "warn",
        summary: "Preferencias de marketing",
        detail: "No se pudo identificar tu usuario. Inicia sesión nuevamente.",
        life: 3500,
      });
      return;
    }
    setMarketingAccepted(checked);
    try {
      await actualizarMarketing(userId, checked);
      toastRef.current?.show({
        severity: "success",
        summary: "Preferencias de marketing",
        detail: checked
          ? "Aceptaste recibir información y promociones."
          : "Dejaste de aceptar marketing.",
        life: 3000,
      });
    } catch (error) {
      setMarketingAccepted(!checked);
      toastRef.current?.show({
        severity: "error",
        summary: "Preferencias de marketing",
        detail:
          error?.message ||
          "No fue posible actualizar tu preferencia de marketing.",
        life: 3500,
      });
    }
  };

  useEffect(() => {
    if (!userCodigo) return;
    const loadClientInfo = async () => {
      setIsLoadingClientInfo(true);
      try {
        const response = await getInfoCliente(userCodigo);
        setClientInfo(response?.datos?.datoCliente || null);
        setClientData(response?.datos?.cliente || null);
        setDeudaCliente(Array.isArray(response?.datos?.deudaCliente) ? response.datos.deudaCliente : []);
      } catch {
        setClientInfo(null);
        setClientData(null);
        setDeudaCliente([]);
      } finally {
        setIsLoadingClientInfo(false);
      }
    };
    loadClientInfo();
  }, [userCodigo]);

  const loadPurchaseHistory = async () => {
    setIsLoadingHistory(true);
    setHistoryNotice("");
    try {
      const params = new URLSearchParams();
      params.append("fechaDesde", formatHistoryDate(purchasesFilters.desde));
      params.append("fechaHasta", formatHistoryDate(purchasesFilters.hasta));
      params.append("codigo", userCodigo);
      if (purchasesFilters.nroPV?.trim())
        params.append("folioDesde", purchasesFilters.nroPV.trim());
      const response = await getReporteCotizacion(params);
      const rows = Array.isArray(response)
        ? response
        : Array.isArray(response?.cotizaciones)
        ? response.cotizaciones
        : Array.isArray(response?.clientes)
        ? response.clientes
        : [];
      setPurchaseHistory(rows);
      if (!rows.length)
        setHistoryNotice(
          "No se encontraron compras para el rango seleccionado."
        );
    } catch {
      setPurchaseHistory([]);
      setHistoryNotice("No fue posible cargar el historial de compras.");
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handleSavePersonal = async () => {
    const phone = sanitizePhoneNumber(personalForm.telefono);
    if ( !personalForm.nombre.trim() || !personalForm.telefono.trim() || !personalForm.email.trim()) {
      toastRef.current?.show({
        severity: "warn",
        summary: "Campos requeridos",
        detail: "Completa nombre, teléfono y email.",
        life: 3500,
      });
      return;
    }
    if (!/^\d{1,11}$/.test(phone)) {
      toastRef.current?.show({
        severity: "warn",
        summary: "Teléfono inválido",
        detail:
          "El teléfono debe contener solo números y un máximo de 11 dígitos.",
        life: 3500,
      });
      return;
    }
    if (!isValidEmail(personalForm.email)) {
      toastRef.current?.show({
        severity: "warn",
        summary: "Correo inválido",
        detail: "Ingresa un correo válido.",
        life: 3500,
      });
      return;
    }
    setIsSavingPersonal(true);
    try {
      await grabarUsuario(buildUserPayload());
      toastRef.current?.show({
        severity: "success",
        summary: "Éxito",
        detail: "Datos personales actualizados correctamente.",
        life: 3000,
      });
    } catch {
      toastRef.current?.show({
        severity: "error",
        summary: "Error",
        detail: "No fue posible guardar los datos personales.",
        life: 4000,
      });
    } finally {
      setIsSavingPersonal(false);
    }
  };

  const handleSaveAccess = async () => {
    if (!personalForm.telefono.trim() || !personalForm.email.trim()) {
      toastRef.current?.show({
        severity: "warn",
        summary: "Datos requeridos",
        detail:
          "Debes ingresar teléfono y correo en Datos personales antes de cambiar tu clave.",
        life: 4000,
      });
      return;
    }
    if (
      !accessForm.usuario.trim() ||
      !accessForm.contrasenaActual.trim() ||
      !accessForm.contrasena.trim() ||
      !accessForm.confirmarContrasena.trim()
    ) {
      toastRef.current?.show({
        severity: "warn",
        summary: "Campos requeridos",
        detail:
          "Completa usuario, contraseña actual, nueva contraseña y confirmación.",
        life: 3500,
      });
      return;
    }
    if (
      accessForm.contrasenaActual !== session.get(StorageKeys.AUTH_PASSWORD)
    ) {
      toastRef.current?.show({
        severity: "warn",
        summary: "Contraseña incorrecta",
        detail:
          "La contraseña actual no coincide con tu contraseña registrada.",
        life: 3500,
      });
      return;
    }
    if (accessForm.contrasena === accessForm.contrasenaActual) {
      toastRef.current?.show({
        severity: "warn",
        summary: "Contraseña repetida",
        detail: "La nueva contraseña debe ser diferente a la actual.",
        life: 3500,
      });
      return;
    }
    const passwordErrors = validatePasswordWithFeedback(accessForm.contrasena);
    if (passwordErrors.length > 0) {
      toastRef.current?.show({
        severity: "warn",
        summary: "Contraseña insegura",
        detail: passwordErrors[0],
        life: 3500,
      });
      return;
    }
    if (accessForm.contrasena !== accessForm.confirmarContrasena) {
      toastRef.current?.show({
        severity: "warn",
        summary: "Contraseñas no coinciden",
        detail: "La confirmación de la contraseña no coincide.",
        life: 3500,
      });
      return;
    }
    setIsSavingAccess(true);
    try {
      await grabarUsuario(buildUserPayload(true));
      session.set(StorageKeys.AUTH_USERNAME, accessForm.usuario.trim());
      session.set(StorageKeys.AUTH_PASSWORD, accessForm.contrasena.trim());
      startSession({
        user: accessForm.usuario.trim(),
        perfil: appPerfil,
        canal: getAffariCanal(),
        rolCredito: appRolCredito,
      });
      setAccessForm((prev) => ({
        ...prev,
        contrasenaActual: "",
        contrasena: "",
        confirmarContrasena: "",
      }));
      toastRef.current?.show({
        severity: "success",
        summary: "Éxito",
        detail: "Usuario y contraseña actualizados correctamente.",
        life: 3000,
      });
    } catch {
      toastRef.current?.show({
        severity: "error",
        summary: "Error",
        detail: "No fue posible guardar el acceso.",
        life: 4000,
      });
    } finally {
      setIsSavingAccess(false);
    }
  };

  const renderSectionContent = () => {
    if (activeSection === "personal") {
      return (
        <div className="flex flex-column gap-3">
          <PersonalInfoForm
            clientData={clientData}
            clientInfo={clientInfo}
            isLoadingClientInfo={isLoadingClientInfo}
            perfilForm={perfilForm}
            personalForm={personalForm}
            isSavingPersonal={isSavingPersonal}
            onUpdateField={updatePersonalForm}
            onSave={handleSavePersonal}
            marketingAccepted={marketingAccepted}
            onMarketingChange={handleMarketingChange}
          />
          <SecurityForm
            accessForm={accessForm}
            isSavingAccess={isSavingAccess}
            onUpdateField={updateAccessForm}
            onSave={handleSaveAccess}
          />
        </div>
      );
    }
    if (activeSection === "purchases") {
      return (
        <PurchaseHistory
          purchasesFilters={purchasesFilters}
          purchaseHistory={purchaseHistory}
          historyNotice={historyNotice}
          isLoadingHistory={isLoadingHistory}
          onUpdateFilter={updatePurchasesFilter}
          onLoadHistory={loadPurchaseHistory}
        />
      );
    }
    if (activeSection === "balance") {
      return (
        <AccountBalance clientInfo={clientInfo} deudaCliente={deudaCliente} isLoadingClientInfo={isLoadingClientInfo} />
      );
    }
    if (activeSection === "help") {
      return <HelpSection />;
    }
    return null;
  };

  return (
    <div className="landing-container">
      <div className="admin-pages-grid">
        <aside className="admin-pages-list">
          <div className="admin-pages-list-header">
            <h2 className="admin-text-lg">
              <i className="pi pi-user" />
              Mi cuenta
            </h2>
          </div>
          <span className="p-input-icon-left admin-pages-search">
            <InputText
              hidden={accountSections.length <= 1}
              className="admin-pages-field admin-pages-search-input admin-text-sm"
              placeholder="Buscar..."
              value={sidebarSearchTerm}
              onChange={(e) => setSidebarSearchTerm(e.target.value)}
            />
          </span>

          {appPerfil === "Administrador" &&
            (isMobile ? (
              <p className="admin-text-sm" style={{ color: "var(--text-color-secondary)", margin: "0.5rem 0", padding: "0 0.5rem",}}>
                Administración y Gestor de Páginas disponibles solo en versión
                web.
              </p>
            ) : (
              <>
                <Button
                  label="Administración"
                  onClick={() => navigate("/administracion/familias")}
                  size="small"
                  rounded
                  icon="pi pi-cog"
                />
                <Button
                  label="Gestor de Páginas"
                  onClick={() => navigate("/gestor-paginas-info")}
                  size="small"
                  rounded
                  icon="pi pi-folder-open"
                  severity="info"
                />
              </>
            ))}
          <div style={{ maxHeight: "calc(100vh - 320px)", overflow: "auto" }}>
            <div className="admin-pages-items-container">
              <aside className="flex flex-column gap-2">
                {filteredAccountSections.map((section) => (
                  <div
                    key={section.id}
                    className={`admin-page-item ${
                      activeSection === section.id ? "active" : ""
                    }`}
                    onClick={() => setActiveSection(section.id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") setActiveSection(section.id);
                    }}
                  >
                    <div className="admin-page-item-content">
                      <div className="admin-page-item-icon">
                        <i className={section.icon} />
                      </div>
                      <div className="admin-page-item-info">
                        <span className="admin-page-item-label admin-text-sm">
                          {section.label}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </aside>
            </div>
          </div>
        </aside>
        <div className="flex-1">
          <section className="admin-pages-form">
            {renderSectionContent()}
          </section>
        </div>
      </div>
      <AppToast ref={toastRef} />
    </div>
  );
};

export default MyAccount;
